'use client';

import { useState, useEffect } from 'react';
import { useReveal } from '@/components/marketing/engine/use-reveal';

/* v7 Fragmentation — conflicting signals resolved into one defensible decision.
   Story beat: FRAGMENTATION. Full-width: narrative · visualization · resolution.
   Crimson = conflict only · Green = resolved/verified. */

interface Conflict {
  claimSrc: string;
  claim: string;
  counterSrc: string;
  counter: string;
  verdict: string;
  conf: number;
}

const CONFLICTS: Conflict[] = [
  { claimSrc: 'Resume', claim: '"8 years of React"',
    counterSrc: 'Assessment', counter: 'Failed hooks live',
    verdict: 'Re-leveled to Mid', conf: 96 },
  { claimSrc: 'Recruiter note', claim: '"Strong communicator"',
    counterSrc: 'Interview', counter: 'Vague under follow-up',
    verdict: 'Routed to panel', conf: 88 },
  { claimSrc: 'Self-report', claim: '"Led the migration"',
    counterSrc: 'Repo + identity', counter: '2 commits, 1 sprint',
    verdict: 'Contribution scoped', conf: 93 },
];

interface ConflictCardProps {
  c: Conflict;
  resolved: boolean;
  i: number;
}

function ConflictCard({ c, resolved, i }: ConflictCardProps) {
  return (
    <div style={{
      position: 'relative', borderRadius: 'var(--r-md)', padding: '16px 18px',
      background: 'var(--surface)',
      border: `1px solid ${resolved ? 'color-mix(in srgb, var(--green) 32%, transparent)' : 'var(--border)'}`,
      transition: 'border-color .7s var(--ease), background .7s',
      overflow: 'hidden',
    }}>
      {/* Raw: claim vs conflicting counter */}
      <div style={{ opacity: resolved ? 0 : 1, transform: resolved ? 'translateY(-6px)' : 'translateY(0)',
        transition: 'opacity .5s var(--ease), transform .5s var(--ease)',
        position: resolved ? 'absolute' : 'relative', inset: resolved ? '16px 18px' : 'auto', pointerEvents: resolved ? 'none' : 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ flex: 1 }}>
            <div className="label" style={{ fontSize: '8px', marginBottom: '4px' }}>{c.claimSrc}</div>
            <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-3)', textDecoration: 'line-through', textDecorationColor: 'var(--text-4)' }}>{c.claim}</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', flexShrink: 0 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--crimson)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 9v4M12 17h.01M10.3 3.9L1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/></svg>
          </div>
          <div style={{ flex: 1, textAlign: 'right' }}>
            <div className="label" style={{ fontSize: '8px', marginBottom: '4px', color: 'var(--crimson)' }}>{c.counterSrc}</div>
            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--crimson)' }}>{c.counter}</div>
          </div>
        </div>
      </div>

      {/* Resolved: reconciled verdict */}
      <div style={{ opacity: resolved ? 1 : 0, transform: resolved ? 'translateY(0)' : 'translateY(6px)',
        transition: `opacity .6s var(--ease) ${0.15 + i * 0.1}s, transform .6s var(--ease) ${0.15 + i * 0.1}s`,
        position: resolved ? 'relative' : 'absolute', inset: resolved ? 'auto' : '16px 18px', pointerEvents: resolved ? 'auto' : 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '28px', height: '28px', borderRadius: '9px', flexShrink: 0, background: 'var(--green-soft)',
            display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--text)' }}>{c.verdict}</div>
            <div className="mono" style={{ fontSize: '9px', color: 'var(--text-3)', marginTop: '2px' }}>{c.claimSrc} ↔ {c.counterSrc} · reconciled</div>
          </div>
          <span className="mono" style={{ fontSize: '12px', fontWeight: 700, color: 'var(--green)' }}>{c.conf}%</span>
        </div>
      </div>
    </div>
  );
}

export function FragmentationSection() {
  const { ref, shown } = useReveal(0.18);
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    if (!shown) return;
    const t = setTimeout(() => setResolved(true), 1800);
    return () => clearTimeout(t);
  }, [shown]);

  const slideIn = (d: number): React.CSSProperties => ({ opacity: shown ? 1 : 0, transform: shown ? 'none' : `translateY(16px)`, transition: `all .9s var(--ease) ${d}s` });

  return (
    <section ref={ref} className="section" style={{
      /* Narrative color-temperature arc — opening beat: a faint crimson "unresolved
         conflict" undertone biased to the left (the problem narrative), layered over
         the shared indigo field. Fully additive: drop the first gradient to revert. */
      background: 'radial-gradient(ellipse 70% 92% at 6% 46%, color-mix(in srgb, var(--crimson) 7%, transparent) 0%, transparent 56%), var(--bg-veil)',
      overflow: 'hidden' }}>
      <div className="section-inner" style={{ maxWidth: '1180px' }}>
        <div className="frag-grid" style={{ display: 'grid', gridTemplateColumns: '0.85fr 1.1fr 0.85fr', gap: '40px', alignItems: 'center' }}>

          {/* LEFT — problem narrative */}
          <div style={slideIn(0.05)}>
            <span className="label" style={{ color: 'var(--crimson)', display: 'block', marginBottom: '16px' }}>01 · FRAGMENTATION</span>
            <h2 className="serif" style={{ fontSize: 'var(--h2)', lineHeight: 1.02, letterSpacing: '-0.01em', marginBottom: '18px' }}>
              Signals that <span style={{ fontStyle: 'italic', color: 'var(--crimson-soft)' }}>don&apos;t agree.</span>
            </h2>
            <p style={{ fontSize: 'var(--body)', color: 'var(--text-2)', lineHeight: 1.65 }}>
              Resumes claim. Interviews impress. Assessments tell a different
              story. Scattered across tools, the evidence contradicts itself.
              The real signal disappears.
            </p>
          </div>

          {/* CENTER — visualization */}
          <div style={slideIn(0.15)}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '18px' }}>
              <div style={{ display: 'inline-flex', padding: '4px', borderRadius: 'var(--r-full)', border: '1px solid var(--border)', background: 'var(--surface)', gap: '2px' }}>
                {['Raw signals', 'VHire resolves'].map((l, idx) => {
                  const on = (resolved ? 1 : 0) === idx;
                  return (
                    <button key={l} onClick={() => setResolved(idx === 1)} style={{
                      padding: '7px 16px', borderRadius: 'var(--r-full)', border: 'none', cursor: 'pointer',
                      fontSize: '12px', fontWeight: on ? 600 : 500,
                      color: on ? (idx === 1 ? 'var(--green)' : 'var(--text)') : 'var(--text-3)',
                      background: on ? 'var(--surface-raised)' : 'transparent', transition: 'all .3s',
                    }}>{l}</button>
                  );
                })}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {CONFLICTS.map((c, i) => <ConflictCard key={i} c={c} resolved={resolved} i={i} />)}
            </div>
            <div style={{ textAlign: 'center', marginTop: '16px' }}>
              <span className="label" style={{ fontSize: '8.5px', color: resolved ? 'var(--green)' : 'var(--crimson)', transition: 'color .5s' }}>
                {resolved ? '✓ 3 conflicts reconciled into verified verdicts' : '⚠ 3 unresolved contradictions'}
              </span>
            </div>
          </div>

          {/* RIGHT — resolution narrative */}
          <div style={{ ...slideIn(0.25), textAlign: 'right' }}>
            <span className="label" style={{ color: 'var(--green)', display: 'block', marginBottom: '16px' }}>RESOLUTION</span>
            <h2 className="serif" style={{ fontSize: 'var(--h2)', lineHeight: 1.02, letterSpacing: '-0.01em', marginBottom: '18px' }}>
              One <span style={{ fontStyle: 'italic', color: 'var(--green-bright)' }}>defensible</span> truth.
            </h2>
            <p style={{ fontSize: 'var(--body)', color: 'var(--text-2)', lineHeight: 1.65 }}>
              VHire weighs every verified signal against the claim, surfaces the
              contradiction, and resolves it into a decision your team can stand
              behind.
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .frag-grid { grid-template-columns: 1fr !important; gap: 36px !important; }
          .frag-grid > div:last-child { text-align: left !important; }
        }
      `}</style>
    </section>
  );
}

export default FragmentationSection;
